import { faBars, faGear, faFolder, faHouse, faScroll, faTimes, faVectorSquare, IconDefinition } from "@fortawesome/pro-regular-svg-icons";
import { faArrowProgress, faDollarSign } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AppBar, Box, Drawer, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, paperClasses, styled, Toolbar, Tooltip, Typography, useTheme } from "@mui/material";
import { FC, useContext, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Permissions } from "../../../api/dtos";
import textLogo from "../../../assets/img/logo-mccloy-text.png";
import PermissionsContext from "../../../contexts/auth/PermissionsContext";
import LotContext from "../../../contexts/data/LotContext";
import ProjectContext from "../../../contexts/data/ProjectContext";
import StageContext from "../../../contexts/data/StageContext";
import Image from "../Image";
import LogoutButton from "./LogoutButton";
import NavigationLotSelector from "./NavigationLotSelector";
import NavigationProjectSelector from "./NavigationProjectSelector";
import NavigationStageSelector from "./NavigationStageSelector";
import ToggleFeedbackButton from "../../sentry/ToggleFeedbackButton";
import { AppSettings } from "../../../AppSettings";

const BAR_HEIGHT = 8;
const OPEN_DRAWER_WIDTH = 30;
const CLOSED_DRAWER_WIDTH = 8;

const IIgnoreDrawerProps = {
    drawerOpen: true
};

const StyledAppBar = styled(AppBar)(({theme}) => ({
    zIndex: theme.zIndex.drawer+1,
    backgroundColor: "#FFFFFF",
}));

const StyledToolbar = styled(Toolbar)(({theme}) => ({
    display: "flex",
    justifyContent: "space-between",
    minHeight: theme.spacing(BAR_HEIGHT),
}));

const StyledLogo = styled(Image)(({theme}) => ({
    paddingRight: theme.spacing(2),
    height: theme.spacing(BAR_HEIGHT),
    cursor:"pointer"
}));

const StyledDrawer = styled(Drawer, {shouldForwardProp: (prop) => !(prop in IIgnoreDrawerProps)})<{drawerOpen?: boolean}>(({theme, drawerOpen}) =>({
    width: drawerOpen ? theme.spacing(OPEN_DRAWER_WIDTH)  : theme.spacing(CLOSED_DRAWER_WIDTH),
    flexShrink: 0,
    overflowX: "hidden",
    transition: drawerOpen
        ? theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        })
        : theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
    [`& .${paperClasses.root}`] : {
        width: drawerOpen ? theme.spacing(OPEN_DRAWER_WIDTH) : theme.spacing(CLOSED_DRAWER_WIDTH),
        overflowX: 'hidden',
        transition: drawerOpen 
            ? theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            })
            : theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            })
    }

}));

const StyledDrawerButton = styled(IconButton) (({theme}) => ({
    marginRight: theme.spacing(2),
    aspectRatio: 1,
    height: theme.spacing(BAR_HEIGHT)
}));

const StyledListItem = styled(ListItem)(({theme}) => ({
    minHeight: theme.spacing(5),
}));

const StyledListItemButton = styled(ListItemButton)(({theme}) => ({
    padding: theme.spacing(0, 2),
    minHeight: theme.spacing(5),
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
}));

const StyledListItemText = styled(ListItemText)(({theme}) => ({
    margin: theme.spacing(0.5, 0),
    lineHeight: 1
    
}));

const StyledListItemIcon = styled(ListItemIcon)(({theme}) => ({
    minWidth: theme.spacing(6),
    fontSize: theme.spacing(3)
}));

interface IDrawerLink {
    buildUrl: (_projectId?: number, _stageId?: number, _lotId?: number) => string, 
    icon: IconDefinition,
    title: string,
    requiredPermissions?: Permissions[]
}

const links : IDrawerLink[] = [ 
    {
        buildUrl: () => `/projects`, 
        icon: faHouse, 
        title:"Projects"
    }, 
    {
        buildUrl: (projectId?: number, _stageId?: number, _lotId?: number) => `/projects/${projectId}/`, 
        icon: faArrowProgress, 
        title:"Stages"
    }, 
    {
        buildUrl: (projectId?: number, stageId?: number, _lotId?: number) => `/projects/${projectId}/stages/${stageId}`, 
        icon: faVectorSquare, 
        title:"Lots"
    }, 
    {
        buildUrl: (projectId?: number, stageId?: number, lotId?: number) => `/projects/${projectId}/stages/${stageId}/lots/${lotId}`, 
        icon: faScroll,
        title: "Contracts"
    },
    {
        buildUrl: () => "/reports",
        icon: faFolder,
        title: "Reports"
    },
    {
        buildUrl: () => "/finances",
        icon: faDollarSign,
        title: "Finances",
        requiredPermissions: [Permissions.ReportsCanViewFinances]
    },
    {
        buildUrl: () => "/admin", 
        icon: faGear, 
        title:"Administration",
        requiredPermissions: [Permissions.AdminAccess]
    }
]


export const NavigationMenu : FC = () => {
    const navigate = useNavigate();

    const theme = useTheme();

    const permissions = useContext(PermissionsContext);

    const [drawerOpen, setDrawerOpen] = useState(false);

    const {data: project} = useContext(ProjectContext);
    const {data: stage} = useContext(StageContext);
    const {data: lot} = useContext(LotContext);

    const menuItems = useMemo(() => {
        let linkComponent = null;

        return links?.map((link, i) => {
            let linkIncluded = true;
            link.requiredPermissions?.forEach(permission => {
                if(!permissions?.data?.includes(permission)) {
                    linkIncluded = false;
                }
            })

            if(!linkIncluded) {
                return "";
            }

            linkComponent = (
                <StyledListItem
                    key={i}
                    disablePadding
                >
                    <StyledListItemButton
                        //@ts-ignore-next-line
                        component={Link}
                        to={
                            link.buildUrl(
                                project?.id ?? undefined,
                                stage?.id ?? undefined,
                                lot?.id ?? undefined,
                            )
                        }
                        sx={{
                            paddingRight: (drawerOpen ? theme.spacing(2) : theme.spacing(0))
                        }}
                    >
                        <StyledListItemIcon>
                            <FontAwesomeIcon fixedWidth icon={link.icon} />
                        </StyledListItemIcon>
                        
                        <StyledListItemText 
                            primary={link.title} 
                            sx={{
                                //display: !drawerOpen ? 'none' : undefined
                            }} 
                        />
                    </StyledListItemButton>
                </StyledListItem>
            );

            if (drawerOpen) {
                return linkComponent;
            }
    
            return (
                <Tooltip title={link.title} placement="right" key={i}>
                    <Box>
                        {linkComponent}
                    </Box>
                </Tooltip>
            );
        });

        
    }, [links, permissions, project, stage, lot]);


    const toggleDrawer = () => {
        setDrawerOpen(current => !current);
    }


    return (
        <>
            <StyledAppBar
                position="fixed"
                color="primary"
            >
                <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}>
                    <Box sx={{
                        display: "flex"
                    }}>
                        <StyledToolbar
                            disableGutters
                        >
                            <StyledDrawerButton
                                sx={{
                                    justifySelf: "flex-start"
                                }}
                                onClick={toggleDrawer}
                            >
                                <FontAwesomeIcon icon={drawerOpen ? faTimes : faBars} />
                            </StyledDrawerButton>
                            <StyledLogo 
                                alt="Logo"
                                src={textLogo}
                                onClick={() => navigate("/projects")}
                            />
                            <Typography 
                                variant="h1"
                                sx={{
                                    fontWeight: theme.typography.fontWeightRegular,
                                    color: theme.palette.primary.light
                                }}
                            >
                                Project Tracking System
                            </Typography>
                        </StyledToolbar>
                    </Box>
                    <Box sx={{
                        display: "flex" 
                    }}>
                        <NavigationLotSelector />
                        <NavigationStageSelector />
                        <NavigationProjectSelector />
                        {
                            AppSettings.sentry.dsn && 
                            AppSettings.sentry.enableReportABug && (
                                <ToggleFeedbackButton />
                            )
                        }
                        <LogoutButton />
                    </Box>
                </Box>
            </StyledAppBar>
            <StyledDrawer variant="permanent" drawerOpen={drawerOpen}>
                <Box 
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        padding: theme.spacing(0, 1),
                        // necessary for content to be below app bar
                        ...(theme.mixins.toolbar as any),
                        minHeight: theme.spacing(BAR_HEIGHT),
                        marginBottom: "2px"
                    }}
                />
                <List disablePadding sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
                    {menuItems}
                </List>
            </StyledDrawer>   
        </>
    );
};